import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import ActionContainer from '../../../../components/ActionContainer';
import Button from '../../../../components/Button';
import ErrorMessage from '../../../../components/ErrorMessage';
import { inCartMiniCollarCount } from '../../../../components/ItemsInCart/CouponLineItemV2';
import CheckoutContext from '../../../../lib/CheckoutContext';
import { addressValidationSchema } from '../../../../lib/addressValidation';
import * as events from '../../../../lib/analytics/events';
import { signup } from '../../../../lib/authentication';
import { skusForCartItem } from '../../../../lib/cart';
import { requiresMarketingConsent } from '../../../../lib/consent';
import {
  BillingAccountCreationError,
  UserExistsSignUpError,
  UserFacingError,
  logInternalError,
} from '../../../../lib/errors';
import { isPreorderSku } from '../../../../lib/isPreorderSku';
import { isExpeditedShippingAvailable } from '../../../../lib/shipping';
import { generateID, validateEmail } from '../../../../lib/util';
import styles from '../../../../styles/form.module.scss';
import * as types from '../../../../types';
import { gqlTypes } from '../../../../types';
import ShippingOptionsList from '../../components/ShippingOptionsList';
import {
  AccountDetails,
  ContactInfo,
  GiftShippingOption,
  ShippingAddressDetails,
  ShippingGiftOption,
  ShippingGiftRecipientInformation,
} from './ShippingFormFields';
import AppPaths from '../../../../AppPaths';
import { useHistory } from 'react-router';
import useShippingOptions from '../../../../hooks/useShippingOptions';
import { getNanoReferralPetNameCookie } from '../../../../lib/util/nanoReferralPetNameCookie';
import { billingAccountQuery } from '../../../../graphql-operations';
import { Query } from '@apollo/client/react/components';
import Loading from '../../../../components/Loading';
import useTenancy from '../../../../hooks/useTenancy';
import { fetchRecaptchaToken } from '../../../../lib/recaptcha';
import { FiCountry, useLocale } from '../../../../contexts/LocaleContext';
import { Trans, useTranslation } from 'react-i18next';
import useGiftCart from '../../../../hooks/useGiftCart';
import { Text } from '@mantine/core';

const DEFAULT_SIGNUP_ERROR_MESSAGE =
  'An error occurred while signing up. Please reload your browser and try again or contact support@tryfi.com if the problem persists.';

interface ShippingAddressFormProps {
  loggedInUserId?: string;
  submitting: boolean;
  onComplete(addressUpdate?: {
    firstName: string;
    lastName: string;
    address: types.Address;
    addressValidationStatus: types.AddressValidationNeedsVerify;
    shippingCode: string;
    giftRecipient: { firstName: string; lastName: string } | null;
  }): void;
}

interface FormFields {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  shippingCode: types.ShippingCode | undefined;
  giftFirstName?: string;
  giftLastName?: string;
}

export default function ShippingAddressForm({ loggedInUserId, submitting, onComplete }: ShippingAddressFormProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { shippingOptions, defaultShippingCode } = useShippingOptions();
  const { cart, checkoutState, checkoutActions, session, requiresShippingDetails } = useContext(CheckoutContext);
  const tenancy = useTenancy();
  const { country, language } = useLocale();
  const { isAllGifts, isOnlySomeGifts } = useGiftCart();
  const passwordlessCheckoutEnabled = useSelector(
    (state: types.AppState) => state.config.siteConfig.passwordlessCheckoutEnabled ?? false,
  );

  let initialFirstName = checkoutState.customerName?.firstName ?? '';
  // If the user is a nano user and the first name is an email, set it to an empty string
  if (getNanoReferralPetNameCookie() && validateEmail(initialFirstName)) {
    initialFirstName = '';
  }

  const initialFormValues: FormFields = useMemo(() => {
    let addressFields = {
      line1: checkoutState.shippingAddress?.line1 ?? '',
      line2: checkoutState.shippingAddress?.line2 ?? '',
      city: checkoutState.shippingAddress?.city ?? '',
      state: checkoutState.shippingAddress?.state ?? '',
      zip: checkoutState.shippingAddress?.zip ?? '',
      phone: checkoutState.shippingAddress?.phone ?? '',
      country: checkoutState.shippingAddress?.country || country,
    };

    // Skip pre-populating address fields if the site country is different from the user's country
    if (checkoutState.shippingAddress?.country.toUpperCase() !== country.toUpperCase()) {
      addressFields = {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        country: country,
      };
    }

    return {
      ...addressFields,
      email: '',
      password: '',
      firstName: initialFirstName ?? '',
      lastName: checkoutState.customerName?.lastName ?? '',
      shippingCode: checkoutState.shippingCode,
    };
  }, [
    checkoutState.shippingAddress,
    checkoutState.customerName,
    checkoutState.shippingCode,
    initialFirstName,
    country,
  ]);

  const [errors, setErrors] = useState<string[]>([]);
  const [errorID, setErrorID] = useState<string | undefined>(undefined);
  const [userExistsSignUpError, setUserExistsSignUpError] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<FormFields>(initialFormValues);
  const [submittingForm, setSubmittingForm] = useState(false);

  const loggedIn = !!session;
  const requiresAccountDetails = !loggedIn;
  const loginUrl = `${AppPaths.Login}?returnTo=${AppPaths.CheckoutShipping}`;

  const requiresState = country === FiCountry.US || country === FiCountry.CA;

  const anyPreorder = useMemo(
    () =>
      Object.values(cart.cartItems)
        .flatMap((cartItem) => skusForCartItem(cartItem))
        .some(isPreorderSku),
    [cart.cartItems],
  );

  const canExpedite = useMemo(() => {
    // If there's no address yet, we don't know if we can or cannot expedite, so don't disable the expedited ones yet.
    if (!checkoutState.shippingAddress && !formValues.line1 && !formValues.line2) {
      return true;
    }

    return isExpeditedShippingAvailable({
      ...checkoutState.shippingAddress,
      ...formValues,
    });
  }, [checkoutState.shippingAddress, formValues]);

  // Update form when checkoutState shippingAddress changes (e.g. it's loaded from the user account)
  useEffect(() => {
    setFormValues(initialFormValues);
  }, [initialFormValues]);

  // Reset form to empty on user change (impersonation)
  const [previousUserId, setPreviousUserId] = useState(loggedInUserId);

  useEffect(() => {
    if (loggedInUserId && loggedInUserId !== previousUserId) {
      setFormValues(initialFormValues);
      setPreviousUserId(loggedInUserId);
    }
  }, [previousUserId, loggedInUserId, initialFormValues]);

  // Automatically switch to standard shipping option if address changes in a way that makes expedited shipping
  // unavailable
  useEffect(() => {
    const selectedShippingOption = shippingOptions.find((so) => so.code === formValues.shippingCode);
    if (selectedShippingOption?.isExpedited && !canExpedite) {
      setFormValues({
        ...formValues,
        shippingCode: defaultShippingCode,
      });
    }
  }, [
    canExpedite,
    checkoutActions,
    dispatch,
    formValues,
    formValues.shippingCode,
    shippingOptions,
    defaultShippingCode,
  ]);

  const onChange = useCallback(
    (fieldName: string, value: string) => {
      setFormValues({
        ...formValues,
        [fieldName]: value,
      });
    },
    [formValues],
  );

  const handleErrors = useCallback(
    (newErrors: string[]) => {
      if (newErrors.length > 0) {
        setErrors(newErrors);
        setErrorID(generateID());

        events.shipping.continueError(newErrors.join('; '));
      }
    },
    [setErrorID, setErrors],
  );

  const [giftShippingOption, setGiftShippingOption] = useState<GiftShippingOption>(
    GiftShippingOption.ShipToYourAddress,
  );

  const isGiftFlow = isAllGifts && giftShippingOption === GiftShippingOption.ShipToSomeoneElse;

  const handleSubmit = useCallback(
    async (recaptchaToken: string) => {
      if (submitting) {
        return;
      }

      setSubmittingForm(true);

      // Reset errors
      setErrors([]);
      setUserExistsSignUpError(false);

      const email = formValues.email.trim();
      const password = formValues.password.trim();
      const firstName = formValues.firstName.trim();
      const lastName = formValues.lastName.trim();
      const shippingCode = formValues.shippingCode;

      const addressInput: gqlTypes.AddressInput = {
        line1: formValues.line1.trim(),
        line2: formValues.line2.trim(),
        city: formValues.city.trim(),
        state: formValues.state.trim(),
        zip: formValues.zip.trim().toUpperCase(),
        phone: formValues.phone.trim(),
        country: formValues.country.trim(),
      };

      // Check if Mini is in cart for VI restriction
      const cartItemsArray = Object.values(cart.cartItems);
      const hasMiniInCart = inCartMiniCollarCount(cartItemsArray) > 0;

      try {
        addressValidationSchema(t, language).validateSync(
          {
            ...addressInput,
            email,
            password,
            firstName,
            lastName,
            shippingCode,
          },
          {
            abortEarly: false,
            context: {
              requiresFirstAndLastName: requiresAccountDetails,
              requiresAccountDetails,
              requiresShippingDetails,
              passwordlessCheckoutEnabled,
              hasMiniInCart,
              requiresState,
            },
          },
        );
      } catch (err) {
        if (err instanceof yup.ValidationError) {
          handleErrors(err.errors);
          setSubmittingForm(false);
          return;
        }

        logInternalError(err);
        handleErrors(['An unknown error occurred.']);
        setSubmittingForm(false);
        return;
      }

      if (requiresAccountDetails) {
        try {
          // Pass null for password when passwordless checkout is enabled
          const passwordToSubmit = passwordlessCheckoutEnabled ? null : password;
          await signup(
            email,
            passwordToSubmit,
            firstName,
            lastName,
            tenancy,
            recaptchaToken,
            passwordlessCheckoutEnabled,
            requiresMarketingConsent(country) ? false : undefined,
          );
        } catch (error) {
          /**
           * Our signup helper will make a couple of API calls
           * 1) create the Fi user account
           * 2) create a Recurly billing account
           *
           * If the second call fails, we want to redirect the user to the login page because they already have a
           * Fi user account so they can't signup again and logging in will make another attempt at creating their
           * Recurly billing account if they don't already have one.
           */
          if (error instanceof BillingAccountCreationError) {
            history.push(loginUrl, {
              billingAccountCreationError: true,
            });
            return;
          }

          if (error instanceof UserExistsSignUpError) {
            // Don't automatically redirect to the login screen for this error. We'll show a special error message
            // with a link to login, but by keeping them here they also have the option to sign up with a different
            // email instead.
            setUserExistsSignUpError(true);
          } else {
            const errorMessage = error instanceof UserFacingError ? error.message : DEFAULT_SIGNUP_ERROR_MESSAGE;
            handleErrors([errorMessage]);
          }

          setSubmittingForm(false);
          return;
        }
      }

      if (!requiresShippingDetails) {
        dispatch(checkoutActions.setCustomerName({ firstName, lastName }));
        onComplete();
        setSubmittingForm(false);
        return;
      }

      onComplete({
        firstName,
        lastName,
        address: addressInput,
        // If this is the first time they're entering an address (i.e. creating a new account), address validation
        // should allow an autocorrect of the address without prompting the user as long as the validation backend
        // said it was a verified address
        addressValidationStatus: requiresAccountDetails
          ? types.AddressValidationStatus.NeedsVerifyWithAutocorrect
          : types.AddressValidationStatus.NeedsVerifyWithPrompt,
        shippingCode: shippingCode!,
        giftRecipient: isGiftFlow
          ? { firstName: formValues.giftFirstName ?? '', lastName: formValues.giftLastName ?? '' }
          : null,
      });
      setSubmittingForm(false);
    },
    [
      submitting,
      formValues.email,
      formValues.password,
      formValues.firstName,
      formValues.lastName,
      formValues.shippingCode,
      formValues.line1,
      formValues.line2,
      formValues.city,
      formValues.state,
      formValues.zip,
      formValues.phone,
      formValues.country,
      formValues.giftFirstName,
      formValues.giftLastName,
      isGiftFlow,
      requiresAccountDetails,
      requiresShippingDetails,
      passwordlessCheckoutEnabled,
      onComplete,
      handleErrors,
      history,
      loginUrl,
      dispatch,
      checkoutActions,
      tenancy,
      t,
      cart.cartItems,
      requiresState,
      language,
      country,
    ],
  );

  // wrap handleSubmit in another callback for the form action because onSubmit expects a void return type but
  // handleSubmit is async
  const onSubmitCallback = useCallback(
    (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault();

      fetchRecaptchaToken('signup')
        .then((recaptchaToken) => handleSubmit(recaptchaToken))
        .catch((error) => {
          logInternalError(error, {
            fingerprint: ['{{ default }}', error.message],
            tags: {
              checkoutStep: 'signup',
            },
          });

          // If we were unable to fetch a recaptcha token, show an error message because the backend won't
          // create a user account without one.
          handleErrors([DEFAULT_SIGNUP_ERROR_MESSAGE]);
        });
    },
    [handleErrors, handleSubmit],
  );

  const someGiftsCartDisclaimer = (
    <Text size={16} fw={500} lh={1.4} mb={40} sx={(theme) => ({ color: theme.colors['type-light-secondary'] })}>
      {'All cart items, including gift purchases, will be shipped to a single address.'}
    </Text>
  );
  return (
    <Query<gqlTypes.billingAccount> query={billingAccountQuery} fetchPolicy="network-only">
      {({ data, loading }) => {
        if (loading) {
          return <Loading />;
        }
        return (
          <>
            <form className={styles.form} onSubmit={onSubmitCallback} autoComplete="on">
              {isOnlySomeGifts && someGiftsCartDisclaimer}
              {requiresShippingDetails && isAllGifts && (
                <ShippingGiftOption
                  giftShippingOption={giftShippingOption}
                  setGiftShippingOption={setGiftShippingOption}
                />
              )}
              {requiresAccountDetails && !passwordlessCheckoutEnabled && (
                <AccountDetails
                  onChange={onChange}
                  passwordlessCheckoutEnabled={passwordlessCheckoutEnabled}
                  {...formValues}
                />
              )}
              {!isAllGifts && (
                <ContactInfo
                  onChange={onChange}
                  showPhoneNumberInput={requiresShippingDetails}
                  includeEmailField={requiresAccountDetails && passwordlessCheckoutEnabled}
                  showSignInLink={requiresAccountDetails && passwordlessCheckoutEnabled}
                  {...formValues}
                />
              )}
              {requiresShippingDetails && (
                <>
                  {isGiftFlow && (
                    <ShippingGiftRecipientInformation
                      firstName={formValues.giftFirstName}
                      lastName={formValues.giftLastName}
                      onChange={(fieldName, value) => onChange(fieldName, value)}
                    />
                  )}
                  <ShippingAddressDetails
                    title={isGiftFlow ? "Recipient's address" : t('shipping_address')}
                    includePhoneField={false}
                    onChange={onChange}
                    {...formValues}
                  />
                  <ShippingOptionsList
                    anyPreorder={anyPreorder}
                    canExpedite={canExpedite}
                    currentlySelected={formValues.shippingCode}
                    onSelect={(shippingCode) => onChange('shippingCode', shippingCode)}
                  />
                </>
              )}
              <ActionContainer>
                <Button disabled={submitting || submittingForm} type="submit">
                  {t('save_and_continue')}
                </Button>
              </ActionContainer>
              {/* Special case error because it contains HTML elements (link to login page) */}
              {userExistsSignUpError && (
                <div className={styles.formError}>
                  <Trans
                    i18nKey="a_fi_account_already_exists_for_the_email_address_you_entered_please_sign_in_with_your_existing_account"
                    components={{
                      // eslint-disable-next-line jsx-a11y/anchor-has-content
                      loginLink: <a href={loginUrl} />,
                    }}
                  />
                </div>
              )}
              <ErrorMessage errorID={errorID} errors={errors} />
            </form>
          </>
        );
      }}
    </Query>
  );
}
